import { useEffect, useState } from 'react';
import { EVENTS, type GameState, type AchievementUnlockEntry } from '@flip7/shared';
import { useSound } from '../hooks/useSound';
import { useSocketEvent } from '../hooks/useSocketEvent';
import { useAuth } from '../contexts/AuthContext';
import { Confetti } from '../components/Confetti';
import { Avatar } from '../components/Avatar';

interface GameOverScreenProps {
  gameState: GameState;
  isHost: boolean;
  isSpectator?: boolean;
  onPlayAgain: () => void;
  onToggleWantToPlay?: () => void;
  spectatorId?: string | null;
}

const TIER_COLORS: Record<string, string> = {
  bronze: '#CD7F32',
  silver: '#A0A0A0',
  gold: '#DAA520',
};

export function GameOverScreen({ gameState, isHost, isSpectator, onPlayAgain, onToggleWantToPlay, spectatorId }: GameOverScreenProps) {
  const play = useSound();
  const { user } = useAuth();
  const winner = gameState.players.find(p => p.id === gameState.winnerId);
  const sortedPlayers = [...gameState.players].sort((a, b) => b.totalScore - a.totalScore);
  const [unlocks, setUnlocks] = useState<AchievementUnlockEntry[]>([]);
  const [coinsEarned, setCoinsEarned] = useState<number | null>(null);

  useSocketEvent<{ unlocks: AchievementUnlockEntry[] }>(EVENTS.ACHIEVEMENT_UNLOCKED, (data) => {
    setUnlocks(data.unlocks);
  });

  useSocketEvent<{ coins: number }>(EVENTS.COINS_EARNED, (data) => {
    setCoinsEarned(data.coins);
  });

  useEffect(() => {
    play('gameOver');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="hero-panel">
      {winner && <Confetti />}
      <div className="screen screen-center slide-up" style={{ gap: 'var(--space-2xl)' }}>
      <div className="text-center">
        <h1 className="round-reveal game-over__title">Game Over!</h1>
        <div className="gold-divider" style={{ margin: '10px auto', width: '64px', height: '3px' }} />
        {winner && (
          <>
            <p className="crown-bounce game-over__winner">
              {winner.avatar && <span className="game-over__winner-avatar">{winner.avatar}</span>}
              {winner.name} wins!
            </p>
            {winner.title && (
              <p className="game-over__winner-title">
                {winner.title}
              </p>
            )}
          </>
        )}
      </div>

      {/* Final scores */}
      <div className="game-over__scores-panel">
        <p className="label" style={{ marginBottom: '16px' }}>
          Final Scores
        </p>
        <div className="flex-col" style={{ gap: '10px' }}>
          {sortedPlayers.map((player, rank) => (
            <div
              key={player.id}
              className={`player-row${player.id === gameState.winnerId ? ' player-row--winner' : ''}`}
            >
              <div className="flex-row gap-md">
                <span className={`rank-badge${rank === 0 ? ' rank-badge--first' : ''}`}>
                  {rank + 1}
                </span>
                <span className="player-name">
                  <Avatar config={player.avatarConfig} emoji={player.avatar} size={24} />
                  {player.name}
                </span>
              </div>
              <span className="text-display-score">
                {player.totalScore}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Achievement unlocks */}
      {unlocks.length > 0 && (
        <div className="game-over__achievements-panel">
          <p className="label" style={{ marginBottom: '8px' }}>Achievements Unlocked</p>
          <div className="flex-col" style={{ gap: '6px' }}>
            {unlocks.map((u, i) => (
              <div key={i} className="game-over__achievement-row">
                <span className="game-over__achievement-icon">{u.achievementIcon}</span>
                <div style={{ flex: 1 }}>
                  <span className="game-over__achievement-player">
                    {u.playerName}
                  </span>
                  <span className="game-over__achievement-name">
                    {u.achievementName}
                  </span>
                  <span
                    className="game-over__achievement-tier"
                    style={{ color: TIER_COLORS[u.tier] ?? 'var(--text-muted)' }}
                  >
                    {u.tier}
                  </span>
                </div>
                {u.title && (
                  <span className="game-over__achievement-title">
                    &ldquo;{u.title}&rdquo;
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coins earned */}
      {coinsEarned !== null && (
        <div className="badge-pop coin-badge">
          +{coinsEarned} coins{gameState.pot ? ` (pot: ${gameState.pot})` : ''}
        </div>
      )}

      {/* No coins indicator for bot games */}
      {gameState.hasBots && (
        <p className="game-over__bot-hint">
          No coins earned in games with bots
        </p>
      )}

      {/* Guest sign-up prompt */}
      {!user && !isSpectator && (
        <p className="game-over__guest-prompt">
          Sign in from the home screen to track your achievements and earn titles!
        </p>
      )}

      {isHost ? (
        <button className="btn btn-primary game-over__play-again" onClick={onPlayAgain}>
          Play Again
        </button>
      ) : isSpectator && onToggleWantToPlay ? (
        <SpectatorQueueSection
          gameState={gameState}
          spectatorId={spectatorId}
          onToggleWantToPlay={onToggleWantToPlay}
        />
      ) : (
        <p className="game-over__waiting">
          Waiting for host to start a new game...
        </p>
      )}
      </div>
    </div>
  );
}

function SpectatorQueueSection({ gameState, spectatorId, onToggleWantToPlay }: {
  gameState: GameState;
  spectatorId?: string | null;
  onToggleWantToPlay: () => void;
}) {
  const mySpectator = gameState.spectators.find(s => s.id === spectatorId);
  const queuedSpectators = gameState.spectators
    .filter(s => s.wantsToPlay)
    .sort((a, b) => (a.queuedAt ?? 0) - (b.queuedAt ?? 0));
  const queuePos = mySpectator?.wantsToPlay
    ? queuedSpectators.findIndex(s => s.id === spectatorId) + 1
    : 0;

  return (
    <div className="spectator-queue">
      <button className="btn btn-primary w-full" onClick={onToggleWantToPlay}>
        {mySpectator?.wantsToPlay
          ? 'Cancel \u2014 Stay Spectating'
          : 'I Want to Play Next!'}
      </button>
      <p className="spectator-queue__status">
        {queuePos > 0
          ? `You're #${queuePos} in queue \u2014 you'll join the next game`
          : 'Waiting for host to start a new game...'}
      </p>
    </div>
  );
}
